import React, { useEffect, useRef, useState } from "react";
import { ChevronsDown, ChevronDown, Send, ChevronRight } from "lucide-react";
import { telegramUrl } from "../mock";

// Fixed design size (portrait poster). Whole thing is scaled to fit the viewport
// so it always shows on ONE screen without scrolling.
const DESIGN_W = 480;
const DESIGN_H = 760;

const LandingPage = () => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const fit = () => {
      const s = Math.min(window.innerWidth / DESIGN_W, window.innerHeight / DESIGN_H);
      setScale(s);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
    // DESIGN_W/DESIGN_H are module-level constants and `fit` is defined inside the
    // effect, so no external deps are required. Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire Facebook Pixel engagement events as soon as the page opens (once)
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    if (window.fbq) {
      window.fbq("track", "ViewContent", { content_name: "BDG GAME Landing" });
      window.fbq("trackCustom", "Engagement", { content_name: "Landing Open" });
    }
    // `firedRef` is a stable ref; this must run exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire conversion events + automatically open Telegram on click
  const openTelegram = (e) => {
    if (e) e.preventDefault();
    if (window.fbq) {
      window.fbq("track", "Lead", { content_name: "Join Telegram" });
      window.fbq("track", "Subscribe", { content_name: "Join Telegram" });
      window.fbq("trackCustom", "Engagement", { content_name: "Telegram Click" });
    }
    // Try opening in a new tab (keeps this page alive so the pixel request completes)
    const win = window.open(telegramUrl, "_blank", "noopener,noreferrer");
    // If blocked, redirect same tab but wait ~450ms so the pixel event is actually sent
    if (!win) {
      setTimeout(() => {
        window.location.href = telegramUrl;
      }, 450);
    }
  };

  // Automatically redirect to Telegram 3 seconds after the page opens
  const autoRef = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => {
      if (autoRef.current) return;
      autoRef.current = true;
      if (window.fbq) {
        window.fbq("track", "Lead", { content_name: "Join Telegram (Auto)" });
        window.fbq("track", "Subscribe", { content_name: "Join Telegram (Auto)" });
        window.fbq("trackCustom", "Engagement", { content_name: "Telegram Auto Redirect" });
      }
      // Wait ~800ms AFTER firing the pixel so the events actually reach Facebook
      // before we navigate away (same-tab redirect otherwise cancels the request).
      setTimeout(() => {
        window.location.href = telegramUrl;
      }, 800);
    }, 3000);
    return () => clearTimeout(t);
    // `autoRef` is a stable ref and `telegramUrl` is a module constant; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="poster-viewport">
      <div
        className="poster"
        style={{
          width: DESIGN_W,
          height: DESIGN_H,
          transform: `scale(${scale})`,
        }}
      >
        {/* Community heading */}
        <div className="community-head">
          <p>Join our Telegram community for updates, discussions, and informational content.</p>
          <div className="single-arrow" onClick={openTelegram} role="button" aria-label="Join Telegram">
            <ChevronDown size={44} strokeWidth={3} />
          </div>
        </div>

        {/* Two JOIN TELEGRAM buttons (close together) + down arrows */}
        <div className="cta-block cta-block--top">
          <a
            className="telegram-btn"
            href={telegramUrl}
            target="_blank"
            rel="noreferrer"
            onClick={openTelegram}
          >
            <span className="tg-icon"><Send size={24} /></span>
            <span className="tg-text">JOIN TELEGRAM</span>
            <span className="tg-arrow"><ChevronRight size={20} /></span>
          </a>

          <div className="down-arrow">
            <ChevronsDown className="da-1" size={46} strokeWidth={3} />
            <ChevronsDown className="da-2" size={46} strokeWidth={3} />
          </div>

          <a
            className="telegram-btn"
            href={telegramUrl}
            target="_blank"
            rel="noreferrer"
            onClick={openTelegram}
          >
            <span className="tg-icon"><Send size={24} /></span>
            <span className="tg-text">JOIN TELEGRAM</span>
            <span className="tg-arrow"><ChevronRight size={20} /></span>
          </a>
        </div>

        {/* Disclaimer (for ad compliance) */}
        <div className="disclaimer">
          <p>
            Disclaimer: This platform is for entertainment purposes only. It does
            not guarantee any winnings, income, or results. Please participate
            responsibly and only if permitted by the laws of your region. 18+ only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
